import type { GridBlock as GridBlockType, GridVariant } from '@modules/Nzo.CMS/Frontend/libraries/cmsPage/blocks/blocks.state'
import { BlocksView } from '@modules/Nzo.CMS/Frontend/libraries/cmsPage/blocks/blocks.template'
import { Img } from '@shared/Frontend/components/img'
import { useInView } from '@shared/Frontend/hooks/useInView'
import { pushDatalayer } from '@shared/Frontend/seo/datalayer'
import { cn } from '@shared/Frontend/utils/addToClassName'
import { Clickable } from '@shared/Frontend/utils/clickable'
import { getSite } from '@shared/Frontend/utils/site'
import { slugify } from '@shared/Frontend/utils/slugify'
import { SVGIcon } from '@shared/Frontend/utils/svg'
import React from 'react'
import { BlockActions } from '../shared/blockActions'
import { BlockHeader } from '../shared/blockHeader'

const site = getSite()

export const GridBlock: BlocksView['GridBlock'] = (props) => {
  const { id, title, items, variant } = props
  const { inView, ref } = useInView({ threshold: 0.1 })

  const subHeading = title ? 'h3' : 'h2'

  return (
    <section id={id} ref={ref} className={cn(`block block--grid grid--${variant} ls-spacing`, inView && 'inview')}>
      <div className="block__inner ls-container ls-gutter">
        <BlockHeader {...props} />

        <div className="block__content--full">
          <ListLayout items={items} variant={variant} subHeading={subHeading} listClassName="block__list" />
        </div>

        <BlockActions {...props} datalayerCategory="grid_block" />
      </div>
    </section>
  )
}

export const ListLayout = (props: {
  items: GridBlockType['items']
  variant: GridVariant
  subHeading: 'h2' | 'h3'
  listClassName?: string
}) => {
  const { listClassName, items, variant, subHeading } = props
  return (
    <ul className={`grid gridsys--grid ${listClassName || ''}`}>
      {items.map((item, index) => {
        return <ItemLayout key={`${index}-${item.title}`} {...item} subHeading={subHeading} variant={variant} />
      })}
    </ul>
  )
}

const ItemLayout = (
  props: { subHeading: 'h2' | 'h3'; variant: GridBlockType['variant'] } & GridBlockType['items'][number]
) => {
  const { subHeading, icon, image, introduction, label, link, title, variant } = props
  const hasAction = link && link.url && link.label
  const SubHeading = subHeading
  const { inView, ref } = useInView<HTMLLIElement>({ threshold: 0.2 })
  return (
    <li ref={ref} className={cn('grid__item', inView && 'inview')}>
      <article className="grid__article">
        {variant == 'icon' && icon && (
          <div className="">
            <SVGIcon id={icon} className="grid__icon" size={80} />
          </div>
        )}

        {variant == 'image' && image && (
          <div className="grid__visual grid__visual--image fit-object" aria-hidden="true">
            <Img {...image} loading="lazy" className="grid__image" width="384" height="300" sizes='(min-width: 530px) 384px, 90vw' />
            {site != 'ZEG' && (
              <svg className="grid__shape" width="400" height="318" viewBox="0 0 400 318" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M400 318H0V0H400V318ZM198.521 35.8076C126.299 18.3773 54.078 62.4113 35.7939 134.885C18.4242 207.358 62.3059 279.833 134.527 298.181C206.467 315.543 346.436 316.521 365.599 244.9L366.036 243.148C384.32 170.675 270.742 54.1554 198.521 35.8076Z" /></svg>
            )}
          </div>
        )}

        {(label || title || introduction || hasAction) && (
          <div className="grid__text">
            {(label || title) && (
              <header className="grid__header">
                <div className="grid__header-inner ls-reversed-column">
                  {title && (
                    <SubHeading id={slugify(title)} className="grid__title title--xs">
                      {title}
                    </SubHeading>
                  )}
                  {label && <p className="grid__label text--sm">{label}</p>}
                </div>
              </header>
            )}

            {introduction && <p className="grid__intro">{introduction}</p>}

            {hasAction && (
              <footer className="grid__footer">
                <Clickable
                  elementKind="link"
                  styleKind="link"
                  {...link}
                  className="grid__clickable link link--hollow"
                  hasBlob={true}
                  icon={'icon-chevron-right'}
                  onClick={() =>
                    pushDatalayer({
                      category: `grid_with_${variant}_block`,
                      label: title || '',
                      action: 'grid_item_click',
                    })
                  }
                />
              </footer>
            )}
          </div>
        )}
      </article>
    </li>
  )
}
